/**
 * Paginación de una tabla que ya se tiene entera en memoria.
 *
 * No consulta de nuevo al servidor: recorta lo que la pantalla ya recibió. Es
 * lo que hace falta acá, donde una consulta trae el período completo y el
 * problema es que mil filas de una vez no se leen y trancan el navegador.
 *
 * Si todo entra en una página no se dibuja nada: una barra que dice "1-6 de 6"
 * con los dos botones apagados es ruido.
 */

import { ChevronLeft, ChevronRight } from 'lucide-react';

import { paginaValida, totalDePaginas } from '../lib/paginacion';

export interface PaginacionProps {
  /** Cuántos registros hay en total, no cuántos se están mostrando. */
  readonly total: number;
  /** Página actual, empezando en 1. */
  readonly pagina: number;
  readonly tamanio: number;
  readonly onCambiar: (pagina: number) => void;
  /** Cómo se llama lo que se está paginando: "tramos", "conceptos". */
  readonly etiqueta: string;
}

export function Paginacion({
  total,
  pagina,
  tamanio,
  onCambiar,
  etiqueta,
}: PaginacionProps): React.JSX.Element | null {
  if (total <= tamanio) return null;

  const paginas = totalDePaginas(total, tamanio);
  const actual = paginaValida(pagina, total, tamanio);
  const desde = (actual - 1) * tamanio + 1;
  const hasta = Math.min(actual * tamanio, total);

  return (
    <nav className="paginacion" aria-label={`Paginación de ${etiqueta}`}>
      <span className="paginacion-rango">
        {`${String(desde)}-${String(hasta)} de ${String(total)} ${etiqueta}`}
      </span>

      <div className="paginacion-controles">
        <button
          type="button"
          className="plano"
          disabled={actual === 1}
          aria-label={`Página anterior de ${etiqueta}`}
          onClick={() => onCambiar(actual - 1)}
        >
          <ChevronLeft size={15} aria-hidden="true" />
          Anterior
        </button>

        {/* El número de página se anuncia solo cuando cambia: quien navega con
            lector de pantalla no tiene cómo saber dónde quedó. */}
        <span className="paginacion-pagina" aria-live="polite">
          {`Página ${String(actual)} de ${String(paginas)}`}
        </span>

        <button
          type="button"
          className="plano"
          disabled={actual === paginas}
          aria-label={`Página siguiente de ${etiqueta}`}
          onClick={() => onCambiar(actual + 1)}
        >
          Siguiente
          <ChevronRight size={15} aria-hidden="true" />
        </button>
      </div>
    </nav>
  );
}
